/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import { PrimeTableColumn } from './PrimeTable.types';

/**
 * Get nested property value from object using dot notation
 */
export const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, prop) => current?.[prop], obj);
};

/**
 * Sort data by field
 */
export const sortData = <T>(
  data: T[],
  sortField?: string,
  sortOrder?: 1 | -1
): T[] => {
  if (!data || data.length === 0) return data;

  const sorted = [...data];

  if (sortField && sortOrder) {
    // Single column sorting
    sorted.sort((a, b) => {
      const valueA = getNestedValue(a, sortField);
      const valueB = getNestedValue(b, sortField);
      return sortOrder * compareValues(valueA, valueB);
    });
  }

  return sorted;
};

/**
 * Compare two values for sorting
 */
const compareValues = (a: any, b: any): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b);
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return a === b ? 0 : a ? 1 : -1;
  }

  return String(a).localeCompare(String(b));
};


/**
 * Paginate data
 */
export const paginateData = <T>(data: T[], first: number, rows: number): T[] => {
  if (!data || data.length === 0) return data;
  return data.slice(first, first + rows);
};

/**
 * Export data to CSV
 */
export const exportCSV = <T>(
  data: T[],
  columns: PrimeTableColumn<T>[],
  filename: string = 'export.csv'
): void => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create CSV header
  const headers = columns.map((col) => col.header).join(',');

  // Create CSV rows
  const rows = data.map((item) => {
    return columns
      .map((col) => {
        const value = getNestedValue(item, col.field as string);
        // Escape commas and quotes
        const strValue = String(value ?? '');
        if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
          return `"${strValue.replace(/"/g, '""')}"`;
        }
        return strValue;
      })
      .join(',');
  });

  // Combine headers and rows
  const csv = [headers, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Deep equals comparison for objects
 */
export const deepEquals = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) => deepEquals(a[key], b[key]));
};

/**
 * Check if item is selected
 */
export const isSelected = <T>(
  item: T,
  selection: T | T[] | null,
  dataKey?: string,
  compareBy: 'equals' | 'deepEquals' = 'equals'
): boolean => {
  if (!selection) return false;

  const selections = Array.isArray(selection) ? selection : [selection];

  return selections.some((selected) => {
    if (dataKey) {
      const itemKey = getNestedValue(item, dataKey);
      const selectedKey = getNestedValue(selected, dataKey);
      return itemKey === selectedKey;
    }

    if (compareBy === 'deepEquals') {
      return deepEquals(item, selected);
    }

    return item === selected;
  });
};

/**
 * Toggle selection
 */
export const toggleSelection = <T>(
  item: T,
  currentSelection: T | T[] | null,
  selectionMode: 'single' | 'multiple' | 'checkbox' | undefined,
  dataKey?: string,
  metaKeySelection?: boolean,
  metaKeyPressed?: boolean
): T | T[] | null => {
  if (!selectionMode) return null;

  const currentSelected = Array.isArray(currentSelection)
    ? currentSelection
    : currentSelection
    ? [currentSelection]
    : [];

  const itemSelected = isSelected(item, currentSelection, dataKey);

  if (selectionMode === 'single') {
    return itemSelected ? null : item;
  }

  // Multiple or checkbox mode
  if (metaKeySelection && !metaKeyPressed) {
    // Replace selection if meta key not pressed
    return [item];
  }

  if (itemSelected) {
    // Remove from selection
    return currentSelected.filter((selected) => !isSelected(item, selected, dataKey));
  } else {
    // Add to selection
    return [...currentSelected, item];
  }
};

/**
 * Calculate total pages
 */
export const calculatePageCount = (totalRecords: number, rows: number): number => {
  return Math.ceil(totalRecords / rows);
};

/**
 * Reorder array items
 */
export const reorderArray = <T>(arr: T[], dragIndex: number, dropIndex: number): T[] => {
  if (dragIndex === dropIndex) return arr;

  const newArr = [...arr];
  const [removed] = newArr.splice(dragIndex, 1);
  newArr.splice(dropIndex, 0, removed);

  return newArr;
};
